import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  guessMapping, inboxApi,
  type ContactField, type ImportPreview, type Segment,
} from '@wa/shared';

/**
 * Upload, map, watch.
 *
 * Three steps because the mapping is the step that cannot be skipped: every list a
 * client has is a different spreadsheet, and guessing the columns silently is how
 * a thousand phone numbers land in the name field. The guess is made and shown, and
 * the person confirms it.
 */
type Step =
  | { name: 'choose' }
  | { name: 'map'; preview: ImportPreview; fileName: string }
  | { name: 'running'; jobId: number };

export function ImportDialog({ fields, segments, onClose, onDone, defaultSegmentId = null }: {
  fields: ContactField[];
  segments: Segment[];
  onClose: () => void;
  /** The import has finished (well or badly): refresh what it touched. The dialog stays open with the result. */
  onDone: () => void;
  /** Opened from a list: that list is preselected, so the import lands in it. */
  defaultSegmentId?: number | null;
}) {
  const [step, setStep] = useState<Step>({ name: 'choose' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-center" role="dialog" aria-modal="true" aria-label="Import customers">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/40" />

      <div className="relative flex max-h-[88vh] w-full max-w-2xl flex-col rounded-t-xl border border-(--color-line) bg-(--color-surface) shadow-xl sm:rounded-(--radius-panel)">
        <header className="flex shrink-0 items-center justify-between border-b border-(--color-line) px-4 py-3">
          <h2 className="text-lead font-semibold">Import Customers</h2>
          <button
            type="button" onClick={onClose} aria-label="Close"
            className="tap grid size-8 place-items-center rounded-(--radius-field) text-(--color-muted) hover:bg-(--color-sunken)"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="m5 5 10 10M15 5 5 15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="pane-scroll min-h-0 flex-1 overflow-y-auto p-4">
          {error && (
            <p role="alert" className="mb-3 rounded-(--radius-field) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
              {error}
            </p>
          )}

          {step.name === 'choose' && (
            <ChooseFile
              busy={busy}
              onFile={async (file) => {
                setBusy(true); setError(null);
                try {
                  const preview = await inboxApi.previewImport(file);
                  setStep({ name: 'map', preview, fileName: file.name });
                } catch (e) {
                  setError(e instanceof Error ? e.message : 'Could not read that file.');
                } finally { setBusy(false); }
              }}
            />
          )}

          {step.name === 'map' && (
            <MapColumns
              defaultSegmentId={defaultSegmentId}
              preview={step.preview}
              fields={fields}
              segments={segments}
              busy={busy}
              onBack={() => setStep({ name: 'choose' })}
              onStart={async (mapping, segmentId) => {
                setBusy(true); setError(null);
                try {
                  const job = await inboxApi.createImport({
                    fileName: step.fileName, content: step.preview.content, mapping, segmentId,
                  });
                  setStep({ name: 'running', jobId: job.id });
                } catch (e) {
                  setError(e instanceof Error ? e.message : 'Could not start the import.');
                } finally { setBusy(false); }
              }}
            />
          )}

          {step.name === 'running' && <Progress jobId={step.jobId} onDone={onDone} onClose={onClose} />}
        </div>
      </div>
    </div>
  );
}

function ChooseFile({ busy, onFile }: { busy: boolean; onFile: (file: File) => void }) {
  const [over, setOver] = useState(false);
  const [fetchingSample, setFetchingSample] = useState(false);
  // Built by the API, not here: it carries this account's custom fields as
  // columns, and its phone column is text so Excel never shows 9.2E+11.
  const sample = async () => {
    setFetchingSample(true);
    try {
      const { blob, fileName } = await inboxApi.importSample();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url; link.download = fileName || 'customers-sample.xlsx'; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } finally {
      setFetchingSample(false);
    }
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
      className={`flex flex-col items-center rounded-(--radius-panel) border border-dashed px-4 py-7 text-center ${over ? 'border-(--color-brand) bg-(--color-brand-wash)' : 'border-(--color-line) bg-(--color-paper)'}`}
    >
      <IconSheet />
      <p className="mt-3 text-body font-semibold">Drag &amp; Drop your file here</p>
      <p className="mt-0.5 text-small text-(--color-muted)">Excel (.xlsx) or CSV, with a header row and a phone column that includes the country code</p>
      <label className="tap mt-4 inline-flex h-10 cursor-pointer items-center rounded-(--radius-field) bg-(--color-brand) px-4 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong)">
        {busy ? 'Reading…' : 'Select file from computer'}
        <input type="file" accept=".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" disabled={busy} className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
      </label>
      <p className="my-3 flex w-40 items-center gap-3 text-small text-(--color-faint)"><span className="h-px flex-1 bg-(--color-line)" />or<span className="h-px flex-1 bg-(--color-line)" /></p>
      <button type="button" onClick={() => void sample()} disabled={fetchingSample} className="tap inline-flex h-10 items-center gap-2 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-4 text-body font-medium text-(--color-ink) hover:bg-(--color-sunken) disabled:opacity-50">
        <IconFile /> {fetchingSample ? 'Preparing…' : 'Download sample (Excel)'}
      </button>
      <p className="mt-3 max-w-md text-micro text-(--color-muted)">
        The sample has a column for every custom field on this account. Up to 8MB.
      </p>
    </div>
  );
}

const IconSheet = () => (
  <svg viewBox="0 0 40 40" className="size-10" aria-hidden="true">
    <path d="M14 5h13l7 7v23H14z" fill="#fff" stroke="#B9B9B9" strokeWidth="1.5" />
    <path d="M27 5v7h7" fill="none" stroke="#B9B9B9" strokeWidth="1.5" />
    <rect x="5" y="13" width="18" height="18" rx="2" fill="#1D6F42" />
    <path d="m10 18 8 8M18 18l-8 8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);
const IconFile = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M5 3h7l4 4v10H5z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /><path d="M12 3v4h4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>;

function MapColumns({ preview, fields, segments, busy, onBack, onStart, defaultSegmentId }: {
  preview: ImportPreview;
  fields: ContactField[];
  segments: Segment[];
  busy: boolean;
  onBack: () => void;
  onStart: (mapping: Record<string, number>, segmentId: number | null) => void;
  defaultSegmentId?: number | null;
}) {
  const [mapping, setMapping] = useState<Record<string, number>>(() => guessMapping(preview.headers, fields));
  const [segmentId, setSegmentId] = useState<number | null>(defaultSegmentId ?? null);

  const targets = [
    { key: 'phone', label: 'Phone number', required: true },
    { key: 'name', label: 'Name', required: false },
    ...fields.map((f) => ({ key: f.fieldKey, label: f.label, required: false })),
  ];

  const set = (key: string, index: number) =>
    setMapping((prev) => {
      const next = { ...prev };
      if (index < 0) delete next[key]; else next[key] = index;
      return next;
    });

  // Static segments only: adding to a dynamic one is meaningless — its membership
  // is a question, not a list, and imported rows either answer it or do not.
  const staticSegments = segments.filter((s) => s.kind === 0);

  return (
    <div>
      <p className="text-small text-(--color-ink-soft)">
        {preview.totalRows.toLocaleString('en-IN')} rows. Check the columns below.
      </p>

      <div className="mt-3 flex flex-col gap-2">
        {targets.map((target) => (
          <label key={target.key} className="flex items-center gap-3">
            <span className="w-32 shrink-0 truncate text-small sm:w-40">
              {target.label}
              {target.required && <span className="text-(--color-bad)"> *</span>}
            </span>
            <select
              value={mapping[target.key] ?? -1}
              onChange={(e) => set(target.key, Number(e.target.value))}
              className="min-w-0 flex-1 rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2 py-1.5 text-small"
            >
              <option value={-1}>Not imported</option>
              {preview.headers.map((header, index) => (
                <option key={index} value={index}>{header || `Column ${index + 1}`}</option>
              ))}
            </select>
          </label>
        ))}
      </div>

      {staticSegments.length > 0 && (
        <label className="mt-4 flex items-center gap-3">
          <span className="w-32 shrink-0 text-small sm:w-40">Add to list</span>
          <select
            value={segmentId ?? -1}
            onChange={(e) => setSegmentId(Number(e.target.value) < 0 ? null : Number(e.target.value))}
            className="min-w-0 flex-1 rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2 py-1.5 text-small"
          >
            <option value={-1}>Nothing</option>
            {staticSegments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      )}

      <div className="mt-4 overflow-x-auto rounded-(--radius-field) border border-(--color-line)">
        <table className="w-full text-micro">
          <thead className="bg-(--color-sunken) text-(--color-muted)">
            <tr>{preview.headers.map((h, i) => <th key={i} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-(--color-line-dim)">
            {preview.sample.map((row, i) => (
              <tr key={i}>
                {preview.headers.map((_, c) => (
                  <td key={c} className="px-2 py-1.5 whitespace-nowrap">{row[c] ?? ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button type="button" onClick={onBack} className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-small">
          Back
        </button>
        <button
          type="button"
          disabled={busy || mapping.phone === undefined}
          onClick={() => onStart(mapping, segmentId)}
          className="tap ml-auto rounded-(--radius-field) bg-(--color-brand) px-3 py-2 text-small font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Starting…' : `Import ${preview.totalRows.toLocaleString('en-IN')} rows`}
        </button>
      </div>
    </div>
  );
}

/**
 * The job as it runs.
 *
 * Polled rather than pushed: it is one screen, open for as long as somebody is
 * looking at it, and adding an import channel to the realtime hub to save a poll
 * every one and a half seconds would be more moving parts than the thing is worth.
 */
function Progress({ jobId, onDone, onClose }: { jobId: number; onDone: () => void; onClose: () => void }) {
  const job = useQuery({
    queryKey: ['import', jobId],
    queryFn: () => inboxApi.import(jobId),
    refetchInterval: (q) => {
      const status = q.state.data?.job.status;
      return status === 2 || status === 3 ? false : 1500;
    },
  });

  const data = job.data?.job;
  const finished = data?.status === 2 || data?.status === 3;

  // Told once, from an effect — not from render, where calling the parent's
  // setState is a React error and, because both callers closed the dialog on it,
  // unmounted the result before anybody could read how many rows were skipped.
  // The dialog stays open; "Done" closes it.
  const told = useRef(false);
  useEffect(() => {
    if (finished && !told.current) { told.current = true; onDone(); }
  }, [finished, onDone]);

  const total = data?.totalRows ?? 0;
  const done = data?.processed ?? 0;
  const percent = total > 0 ? Math.round((done / total) * 100) : data?.status === 2 ? 100 : 0;

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <p className="text-body font-medium">
          {data?.status === 3 ? 'The import failed' : data?.status === 2 ? 'Imported' : 'Importing…'}
        </p>
        <p className="text-micro tabular-nums text-(--color-muted)">
          {done.toLocaleString('en-IN')} of {total.toLocaleString('en-IN')}
        </p>
      </div>

      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-(--color-sunken)">
        <div
          className={`h-full rounded-full transition-[width] duration-300 ${data?.status === 3 ? 'bg-(--color-bad)' : 'bg-(--color-brand)'}`}
          style={{ width: `${percent}%` }}
        />
      </div>

      {data?.lastError && (
        <p role="alert" className="mt-3 rounded-(--radius-field) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {data.lastError}
        </p>
      )}

      <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
        <Stat label="New" value={data?.imported ?? 0} />
        <Stat label="Updated" value={data?.updated ?? 0} />
        <Stat label="Skipped" value={data?.skipped ?? 0} tone={(data?.skipped ?? 0) > 0 ? 'bad' : undefined} />
      </dl>

      {(job.data?.problems.length ?? 0) > 0 && (
        <div className="mt-4">
          <p className="text-small font-medium">Rows that did not go in</p>
          <p className="mt-0.5 text-micro text-(--color-muted)">
            Line numbers count from the top of the file, so they match what a
            spreadsheet shows.
          </p>
          <ul className="mt-2 flex max-h-56 flex-col gap-1 overflow-y-auto rounded-(--radius-field) border border-(--color-line) p-2">
            {job.data?.problems.map((problem) => (
              <li key={problem.lineNumber} className="flex gap-2 text-micro">
                <span className="w-12 shrink-0 tabular-nums text-(--color-muted)">Row {problem.lineNumber}</span>
                <span className="min-w-0 flex-1">{problem.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {finished && (
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={onClose} className="tap rounded-(--radius-field) bg-(--color-brand) px-3 py-2 text-small font-medium text-white">
            Done
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'bad' }) {
  return (
    <div className="rounded-(--radius-field) border border-(--color-line) py-2">
      <dd className={`text-title font-semibold tabular-nums ${tone === 'bad' ? 'text-(--color-bad)' : ''}`}>
        {value.toLocaleString('en-IN')}
      </dd>
      <dt className="text-micro text-(--color-muted)">{label}</dt>
    </div>
  );
}
